import React, { useState, useEffect } from "react";
import { orchestratorApi } from "../api/orchestrator";

// Use relative URLs in production, localhost in development
const API_BASE = process.env.NODE_ENV === "production" ? "" : "http://localhost:8081";

interface AgentPromptSettings {
  agent_name: string;
  qualifier_ai_prompt: string;
  llm_provider: string;
  llm_model: string;
  llm_temperature: number;
  llm_max_tokens: number;
  updated_at: string;
}

export const SystemPrompt: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AgentPromptSettings | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [aiPrompt, setAiPrompt] = useState("");

  useEffect(() => {
    loadPrompt();
  }, []);

  const loadPrompt = async () => {
    try {
      setLoading(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/agents/settings/qualifier_agent`, { headers: authHeaders });
      const data = await response.json();

      if (data.success && data.settings) {
        setSettings(data.settings);
        setAiPrompt(data.settings.qualifier_ai_prompt || '');
      }
    } catch (error) {
      console.error("Failed to load system prompt:", error);
      showMessage('error', 'Failed to load system prompt');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSavePrompt = async () => {
    try {
      setSaving(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/agents/settings/qualifier_agent/prompt`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt }),
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', 'System prompt updated successfully');
        await loadPrompt();
      } else {
        showMessage('error', data.error || 'Failed to update prompt');
      }
    } catch (error) {
      console.error("Failed to update prompt:", error);
      showMessage('error', 'Failed to update prompt');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = settings && aiPrompt !== (settings.qualifier_ai_prompt || '');

  if (loading) {
    return (
      <div className="system-prompt-container">
        <div className="loading-state">Loading system prompt...</div>
      </div>
    );
  }

  return (
    <div className="system-prompt-container">
      <div className="system-prompt-header">
        <h2>📝 AI System Prompt</h2>
        <p className="system-prompt-description">
          Define how the AI analyzes conversations for lead qualification. The prompt instructs the AI on what information to extract and how to format responses.
        </p>
      </div>

      {message && (
        <div className={`message-banner ${message.type}`}>
          {message.type === 'success' ? '✅ ' : '❌ '}
          {message.text}
        </div>
      )}

      <div className="prompt-sections">
        {/* Current Configuration Display */}
        {settings && (
          <div className="config-section current-config">
            <h3>📊 Current AI Configuration</h3>
            <div className="config-display">
              <div className="config-row">
                <span className="config-label">Provider:</span>
                <span className="config-value">{settings.llm_provider}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Model:</span>
                <span className="config-value">{settings.llm_model}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Temperature:</span>
                <span className="config-value">{settings.llm_temperature}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Max Tokens:</span>
                <span className="config-value">{settings.llm_max_tokens}</span>
              </div>
              <div className="config-row">
                <span className="config-label">Last Updated:</span>
                <span className="config-value">{new Date(settings.updated_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {/* AI Prompt Configuration Section */}
        <div className="config-section">
          <div className="section-header">
            <h3>Lead Qualifier Agent Prompt</h3>
            <span className="section-hint">
              Edit the system prompt that guides AI behavior for lead qualification
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="ai-prompt-textarea">System Prompt</label>
            <textarea
              id="ai-prompt-textarea"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={20}
              className="form-control prompt-textarea"
              placeholder="Enter the AI system prompt for lead qualification..."
            />
            <small className="form-hint">
              Use {'{conversationText}'} as a placeholder for the chat messages.
              The AI will analyze the conversation and return qualification results including:
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Lead qualification status (qualified/unqualified)</li>
                <li>Key information extracted (name, phone, email, service interest)</li>
                <li>Next recommended action</li>
                <li>Confidence score</li>
              </ul>
            </small>
          </div>

          <div className="form-actions">
            <button
              onClick={handleSavePrompt}
              disabled={!hasChanges || saving}
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : 'Save AI Prompt'}
            </button>
            {hasChanges && (
              <span className="changes-indicator">● Unsaved changes</span>
            )}
          </div>
        </div>

        {/* Prompt Tips Section */}
        <div className="config-section tips-section">
          <h3>💡 Prompt Engineering Tips</h3>
          <div className="tips-grid">
            <div className="tip-card">
              <h4>🎯 Be Specific</h4>
              <p>Clearly define what constitutes a qualified lead for your business context.</p>
            </div>
            <div className="tip-card">
              <h4>📋 Define Structure</h4>
              <p>Specify the exact output format you need (JSON, key-value pairs, etc.).</p>
            </div>
            <div className="tip-card">
              <h4>🔍 Key Information</h4>
              <p>List the specific data points to extract: name, phone, service type, budget, timeline.</p>
            </div>
            <div className="tip-card">
              <h4>⚠️ Edge Cases</h4>
              <p>Include instructions for handling incomplete information or ambiguous responses.</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .system-prompt-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 24px;
        }

        .system-prompt-header {
          margin-bottom: 32px;
        }

        .system-prompt-header h2 {
          font-size: 28px;
          font-weight: bold;
          margin: 0 0 8px 0;
          color: #1a202c;
        }

        .system-prompt-description {
          font-size: 16px;
          color: #718096;
          margin: 0;
        }

        .message-banner {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 24px;
          font-weight: 500;
        }

        .message-banner.success {
          background-color: #c6f6d5;
          color: #22543d;
          border: 1px solid #9ae6b4;
        }

        .message-banner.error {
          background-color: #fed7d7;
          color: #742a2a;
          border: 1px solid #fc8181;
        }

        .prompt-sections {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .config-section {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .config-section h3 {
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 16px 0;
          color: #2d3748;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid #e2e8f0;
        }

        .section-header h3 {
          font-size: 20px;
          font-weight: 600;
          margin: 0;
          color: #2d3748;
        }

        .section-hint {
          font-size: 14px;
          color: #718096;
        }

        .current-config {
          background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
        }

        .config-display {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }

        .config-row {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .config-label {
          font-size: 12px;
          font-weight: 600;
          color: #718096;
          text-transform: uppercase;
        }

        .config-value {
          font-size: 14px;
          color: #2d3748;
          font-weight: 500;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          color: #2d3748;
          font-size: 14px;
        }

        .form-control {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
        }

        .prompt-textarea {
          font-family: 'Courier New', monospace;
          font-size: 13px;
          line-height: 1.6;
          resize: vertical;
          min-height: 400px;
        }

        .form-hint {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          color: #718096;
        }

        .form-hint ul {
          margin: 0;
          padding-left: 20px;
        }

        .form-hint li {
          margin: 4px 0;
        }

        .form-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 24px;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .btn-primary {
          background-color: #4299e1;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #3182ce;
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .changes-indicator {
          color: #ed8936;
          font-size: 12px;
          font-weight: 600;
        }

        .tips-section {
          background: linear-gradient(135deg, #fff5f5 0%, #fffaf0 100%);
          border: 1px solid #fed7aa;
        }

        .tips-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-top: 16px;
        }

        .tip-card {
          background: white;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid #fed7aa;
        }

        .tip-card h4 {
          font-size: 16px;
          margin: 0 0 8px 0;
          color: #2d3748;
        }

        .tip-card p {
          font-size: 13px;
          color: #718096;
          margin: 0;
          line-height: 1.5;
        }

        .loading-state {
          text-align: center;
          padding: 48px;
          color: #718096;
          font-size: 16px;
        }
      `}</style>
    </div>
  );
};
